// utils/productHelpers.js
import { createMultilingualSlug } from './multilingualUtils';

/**
 * Create a new multilingual product structure
 * @param {object} productData - Basic product data
 * @returns {object} Multilingual product structure
 */
export const createMultilingualProduct = (productData = {}) => {
    return {
        // Multilingual fields - initialize with empty objects
        name: productData.name || { en: '', ar: '' },
        description: productData.description || { en: '', ar: '' },

        // Standard fields
        price: productData.price || 0,
        comparePrice: productData.comparePrice || '',
        cost: productData.cost || '',
        sku: productData.sku || '',
        barcode: productData.barcode || '',
        quantity: productData.quantity || 0,
        category: productData.category || '',
        status: productData.status || 'draft',

        // Arrays
        images: productData.images || [],
        tags: productData.tags || [],
        options: productData.options || [],
        variants: productData.variants || [],
        displayVariants: productData.displayVariants || [],

        // Nested objects
        seo: {
            title: productData.seo?.title || { en: '', ar: '' },
            description: productData.seo?.description || { en: '', ar: '' },
            keywords: productData.seo?.keywords || { en: '', ar: '' }
        },

        shippingInfo: {
            weight: productData.shippingInfo?.weight || '',
            width: productData.shippingInfo?.width || '',
            height: productData.shippingInfo?.height || '',
            length: productData.shippingInfo?.length || ''
        },

        // Timestamps
        createdAt: new Date(),
        updatedAt: new Date()
    };
};

/**
 * Create a multilingual product option
 * @param {string} optionId - Unique option ID
 * @param {object} nameData - Multilingual name object
 * @returns {object} Multilingual option structure
 */
export const createMultilingualOption = (optionId, nameData) => {
    return {
        id: optionId,
        name: nameData, // e.g., { en: "Color", ar: "اللون" }
        values: []
    };
};

/**
 * Create a multilingual option value
 * @param {object|string} value - Value text (object for multilingual, string for simple)
 * @returns {string} JSON string of multilingual value
 */
export const createMultilingualOptionValue = (value) => {
    if (typeof value === 'object') {
        // Already multilingual
        return JSON.stringify(value);
    } else {
        // Simple string value - create basic multilingual structure
        return JSON.stringify({
            en: value,
            ar: value // Start with same value, can be edited later
        });
    }
};

/**
 * Create a multilingual display variant
 * @param {string} variantId - Variant ID
 * @param {object} product - Product data
 * @param {object} variant - Variant data
 * @returns {object} Display variant structure
 */
export const createMultilingualDisplayVariant = (variantId, product, variant) => {
    const displayText = {};

    // Generate display text for each language
    const languages = ['en', 'ar'];
    languages.forEach(lang => {
        const parts = [];

        if (product.options && Array.isArray(product.options)) {
            product.options.forEach(option => {
                // Always use English option name as key for looking up variant data
                const optionKey = typeof option.name === 'string'
                    ? option.name.toLowerCase()
                    : (option.name?.en || Object.values(option.name || {})[0] || '').toLowerCase();

                const value = variant[optionKey];
                if (value) {
                    // Use localized option name for display
                    const optionName = typeof option.name === 'string'
                        ? option.name
                        : (option.name?.[lang] || option.name?.en || Object.values(option.name || {})[0] || '');

                    let displayValue = value;

                    // Handle multilingual values uniformly
                    try {
                        const valueData = JSON.parse(value);
                        if (typeof valueData === 'object') {
                            // Prefer current language, fallback to English, then any available value
                            displayValue = valueData[lang] || valueData.en || Object.values(valueData)[0] || value;
                        }
                    } catch {
                        displayValue = value;
                    }

                    if (optionName && displayValue) {
                        parts.push(`${optionName}: ${displayValue}`);
                    }
                }
            });
        }

        displayText[lang] = parts.join(' • ');
    });

    return {
        variantId: variantId,
        displayText: displayText
    };
};

/**
 * Generate SKU for multilingual product (uses English name)
 * @param {object} product - Product data
 * @param {object} variant - Variant data (optional)
 * @returns {string} Generated SKU
 */
export const generateMultilingualSKU = (product, variant = null) => {
    let baseSKU = '';

    // Use English name for SKU
    const englishName = typeof product.name === 'string'
        ? product.name
        : (product.name?.en || Object.values(product.name || {})[0] || '');

    if (englishName) {
        baseSKU = englishName
            .toUpperCase()
            .replace(/[^A-Z0-9\s]/g, '') // Remove special characters
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .replace(/-+/g, '-') // Replace multiple hyphens with single
            .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    }

    if (variant && baseSKU) {
        // Add variant information to SKU
        const variantParts = [];

        if (product.options && Array.isArray(product.options)) {
            product.options.forEach(option => {
                const optionKey = typeof option.name === 'string'
                    ? option.name.toLowerCase()
                    : (option.name?.en || Object.values(option.name || {})[0] || '').toLowerCase();

                const value = variant[optionKey];
                if (value) {
                    let skuValue = '';

                    // Handle multilingual values uniformly
                    try {
                        const valueData = JSON.parse(value);
                        if (typeof valueData === 'object') {
                            skuValue = valueData.en || Object.values(valueData)[0] || value;
                        } else {
                            skuValue = value;
                        }
                    } catch {
                        skuValue = value;
                    }

                    if (skuValue) {
                        variantParts.push(skuValue.toUpperCase().replace(/[^A-Z0-9]/g, ''));
                    }
                }
            });
        }

        if (variantParts.length > 0) {
            baseSKU += '-' + variantParts.join('-');
        }
    }

    return baseSKU || 'PRODUCT-' + Date.now();
};
